import {
  Stack,
  Select,
  Textarea,
  Group,
  TextInput,
  Button,
  Modal,
  Alert,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { IconInfoCircle } from "@tabler/icons-react"
import { zodResolver } from "mantine-form-zod-resolver"
import { Link } from "react-router-dom"

import { NewMetric, newMetricSchema } from "@costory/types/endpoints/metrics"
import { MetricsDatasourceResponses } from "@costory/types/endpoints/metricsDatasources"
import { CostType } from "@costory/types/prisma-client"

import { TECHNICAL_METRICS } from "@costory/shared/utils/format"

import MetricSelector from "@costory/front/components/dropdown/MetricSelector"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import {
  useUpdateMetricMutation,
  useCreateMetricMutation,
} from "@costory/front/queries/metrics"
import { useMetricsDatasourcesListWithMetricsDefinitionQuery } from "@costory/front/queries/metricsDatasources"

const _MetricForm = ({
  datasources,
  initialValues,
  id,
  setOpened,
}: {
  datasources: MetricsDatasourceResponses.GetAllMetrics
  initialValues?: NewMetric
  id?: string
  setOpened: (opened: boolean) => void
}) => {
  const form = useForm<NewMetric>({
    validate: zodResolver(newMetricSchema),
    initialValues,
    validateInputOnChange: true,
    validateInputOnBlur: true,
  })
  const mutationCreate = useCreateMetricMutation()
  const mutationUpdate = useUpdateMetricMutation()

  const metricsOptions = TECHNICAL_METRICS.map((metric) => ({
    ...metric,
    label: metric.name,
    value: metric.id,
    technical: metric.technical,
    datasource: undefined,
  }))

  if (datasources.length === 0) {
    return (
      <Alert
        icon={<IconInfoCircle size="1rem" />}
        title="No metrics datasources available"
        color="blue"
      >
        You need to create a metrics datasource before you can add a formula.{" "}
        <br />
        Please go to{" "}
        <Link to="/datasources/metrics" style={{ textDecoration: "underline" }}>
          Metrics
        </Link>{" "}
        to set up your first datasource.
      </Alert>
    )
  }

  return (
    <div>
      <Stack justify="space-between" h="100%">
        <Stack>
          <TextInput
            label="Name"
            placeholder="Enter a name"
            {...form.getInputProps("name")}
          />
          <Group>
            <MetricSelector
              metric={form.getInputProps("costType").value}
              setMetric={(value) =>
                form.setFieldValue("costType", value as CostType)
              }
              metricsOptions={metricsOptions}
              onlyCostGroup={true}
              label="Cost Type"
              dropdownHeight={190}
            />
          </Group>
          <Group>
            <Select
              label="Datasource"
              placeholder="Select a datasource"
              {...form.getInputProps("datasourceId")}
              data={datasources.map((datasource) => ({
                value: datasource.id,
                label: datasource.name,
              }))}
            />
            <Select
              w={400}
              label="Metric"
              placeholder="Select a metric"
              {...form.getInputProps("rawMetric")}
              data={datasources
                .filter((el) => el.id == form.getValues().datasourceId)
                .flatMap((datasource) =>
                  (datasource.metadata.metricsDefinition || []).map(
                    (metric) => ({
                      value: metric.name,
                      label: metric.name,
                    }),
                  ),
                )}
            />
          </Group>
          <Textarea
            label="Formula: use cost variable and metric in your equation for example: cost / (metric * 100) "
            placeholder="Enter your formula..."
            minRows={3}
            {...form.getInputProps("formula")}
          />
        </Stack>
        <Group justify="flex-end">
          <Button
            variant="filled"
            disabled={!form.isValid()}
            onClick={() => {
              if (id) {
                mutationUpdate.mutate({ data: form.values, id })
              } else {
                mutationCreate.mutate(form.values)
              }
              setOpened(false)
            }}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </div>
  )
}

export const MetricForm = ({
  opened,
  setOpened,
  initialValues,
  id,
}: {
  opened: boolean
  setOpened: (opened: boolean) => void
  initialValues?: NewMetric
  id?: string
}) => {
  const query = useMetricsDatasourcesListWithMetricsDefinitionQuery()
  return (
    <Modal opened={opened} onClose={() => setOpened(false)} size="xl">
      <QueryWrapper query={query} allowEmptyArray={true}>
        {({ data: datasources }) => (
          <_MetricForm
            datasources={datasources}
            initialValues={initialValues}
            id={id}
            setOpened={setOpened}
          />
        )}
      </QueryWrapper>
    </Modal>
  )
}
